/**
 * WorkerHealthBanner — v2.9.170 (2026-02)
 * ─────────────────────────────────────────────────────────────────────────────
 * Surfaces a high-prominence banner above the Stream Monitor whenever the
 * polling worker is in a degraded state. Four states:
 *
 *   • Healthy    → renders nothing (no chrome noise on the happy path).
 *   • Off-hours  → blue info banner: monitoring is intentionally paused
 *                  because we're outside operating hours. Listeners
 *                  continue sampling. NO auto-restart will fire.
 *   • Stalled    → red banner: one of the loops hasn't ticked in > 120s
 *                  OR its task is dead WHILE it was supposed to be
 *                  running. Operator sees this before the chart shows
 *                  a 2h gap.
 *   • Recovering → amber banner: watchdog has restarted the loop at
 *                  least once in this session; service is alive but
 *                  had a recent stall — useful breadcrumb.
 *
 * Data source: /api/admin/diagnostic/worker-health (polled every 30s
 * by the parent index.js).
 */

import React from 'react';
import { AlertTriangle, Activity, Moon } from 'lucide-react';

const _formatGap = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 90) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(1)} h`;
};

const _formatUptime = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 90) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86400).toFixed(1)} d`;
};

const WorkerHealthBanner = ({ workerHealth }) => {
  if (!workerHealth || workerHealth._diagnostic_ok === false) {
    // Service unreachable — render the most aggressive banner so the
    // operator notices the monitoring system itself is down.
    return (
      <div
        data-testid="worker-health-banner-unreachable"
        className="rounded-lg border border-red-500 bg-red-100/80 dark:bg-red-900/30 text-red-900 dark:text-red-100 px-3 py-2 flex items-center gap-3"
      >
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <div className="text-sm">
          <strong>Worker Health endpoint μη προσπελάσιμο</strong> · Το
          monitoring service ίσως είναι κάτω. Έλεγξε supervisor logs.
        </div>
      </div>
    );
  }

  const mon = workerHealth.monitoring_loop || {};
  const lis = workerHealth.listener_loop || {};
  const opHours = workerHealth.operating_hours || {};
  // v2.9.170 — stalled now respects intent: a loop that is
  // intentionally disabled (enabled=false) is NOT a stall.
  const stalled = (
    mon.stalled
    || lis.stalled
    || (!mon.task_alive && mon.enabled)
    || (!lis.task_alive && lis.enabled)
  );
  const restarts = (mon.watchdog_restarts || 0) + (lis.watchdog_restarts || 0);
  // v2.9.170 — off-hours pause: monitoring intentionally idle outside
  // operating hours. Listener loop typically continues sampling.
  const isOffHoursPause = (
    !stalled
    && !mon.enabled
    && opHours.within_hours === false
    && opHours.always_on === false
  );

  if (stalled) {
    const which = [];
    if (mon.stalled || (!mon.task_alive && mon.enabled)) which.push('Monitoring (τραγούδια)');
    if (lis.stalled || (!lis.task_alive && lis.enabled)) which.push('Listeners');
    return (
      <div
        data-testid="worker-health-banner-stalled"
        className="rounded-lg border-2 border-red-500 bg-red-100/90 dark:bg-red-900/40 text-red-900 dark:text-red-100 px-4 py-3 flex items-start gap-3 shadow-md"
      >
        <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm leading-relaxed">
          <div className="font-semibold text-base">
            ⚠️ Καταγραφή σταματημένη — {which.join(' + ')}
          </div>
          <div className="mt-1 opacity-90">
            Monitoring last tick: <strong>{_formatGap(mon.seconds_since_last_tick)}</strong>
            {' · '}
            Listener last tick: <strong>{_formatGap(lis.seconds_since_last_tick)}</strong>
            {' · '}
            Uptime: <strong>{_formatUptime(workerHealth.uptime_seconds)}</strong>
          </div>
          <div className="mt-1 text-xs opacity-80">
            Ο watchdog ({workerHealth.watchdog?.stall_threshold_seconds || 120}s threshold)
            θα κάνει αυτόματη επανεκκίνηση. Αν συνεχίζεται &gt; 5 min, restart το
            backend service από το supervisor.
          </div>
        </div>
      </div>
    );
  }

  if (isOffHoursPause) {
    return (
      <div
        data-testid="worker-health-banner-off-hours"
        className="rounded-lg border border-sky-400/70 bg-sky-50/80 dark:bg-sky-900/25 text-sky-900 dark:text-sky-100 px-3 py-2 flex items-start gap-3 text-sm"
      >
        <Moon className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div className="flex-1 leading-relaxed">
          <div className="font-semibold">
            🌙 Καταγραφή σε παύση — εκτός ωραρίου ({opHours.label || '—'})
          </div>
          <div className="mt-0.5 text-xs opacity-90">
            Listeners ενεργοί · last tick {_formatGap(lis.seconds_since_last_tick)} πριν.
            Δεν τρέχει αυτόματη επανεκκίνηση. Η καταγραφή τραγουδιών θα ξεκινήσει
            ξανά στις <strong>{(opHours.start ?? 6).toString().padStart(2, '0')}:00</strong>.
          </div>
        </div>
      </div>
    );
  }

  if (restarts > 0) {
    return (
      <div
        data-testid="worker-health-banner-recovering"
        className="rounded-lg border border-amber-400/70 bg-amber-50/80 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 px-3 py-2 flex items-center gap-3 text-sm"
      >
        <Activity className="w-4 h-4 flex-shrink-0" />
        <div className="flex-1">
          <strong>Worker αυτοθεραπεύτηκε</strong> · ο watchdog εντόπισε {restarts}
          {' '}stall(s) και επανεκκίνησε τα loops (uptime{' '}
          {_formatUptime(workerHealth.uptime_seconds)}). Listener tick {_formatGap(lis.seconds_since_last_tick)} πριν,
          Monitoring {_formatGap(mon.seconds_since_last_tick)} πριν.
        </div>
      </div>
    );
  }

  // Healthy — render nothing. The chart and other widgets tell the
  // happy story.
  return null;
};

export default WorkerHealthBanner;
